'use client';

import React, { Component, type ReactNode, type ErrorInfo } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/shadcn/card';
import { createLogger } from '@/shared/lib/logger';
import { formatErrorForDisplay } from '@/shared/lib/error-formatter';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';

const logger = createLogger('ErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  showFullStack: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, showFullStack: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, showFullStack: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error:', error.message, {
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, showFullStack: false });
  };

  toggleStackTrace = () => {
    this.setState({ showFullStack: !this.state.showFullStack });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          showFullStack={this.state.showFullStack}
          onReset={this.handleReset}
          onToggleStack={this.toggleStackTrace}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  showFullStack: boolean;
  onReset: () => void;
  onToggleStack: () => void;
}

// Functional sub-component so useTranslations works inside the class boundary.
function ErrorFallback({
  error,
  showFullStack,
  onReset,
  onToggleStack,
}: ErrorFallbackProps) {
  const t = useTranslations('common');

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {t('errors.title')}
          </CardTitle>
          <CardDescription>{t('errors.generic')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {process.env.NODE_ENV === 'development' && error && (
            <Alert variant="default">
              <AlertTitle>Error details (development only)</AlertTitle>
              <AlertDescription>
                <details className="mt-2">
                  <summary className="cursor-pointer text-form-help text-muted-foreground mb-2">
                    View stack trace
                  </summary>
                  <div className="p-3 bg-muted rounded-md mt-2">
                    <pre className="text-card-meta font-mono text-foreground whitespace-pre-wrap overflow-auto">
                      {showFullStack
                        ? formatErrorForDisplay(error).fullStack
                        : formatErrorForDisplay(error).shortStack}
                    </pre>
                  </div>
                </details>
                {formatErrorForDisplay(error).canShowFull && (
                  <Button
                    onClick={onToggleStack}
                    variant="link"
                    size="sm"
                    className="text-card-meta mt-2 p-0 h-auto"
                  >
                    {showFullStack ? 'Show less' : 'Show full stack trace'}
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex gap-3">
            <Button
              onClick={() => window.location.reload()}
              className="flex-1"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('errors.refresh')}
            </Button>
            <Button
              onClick={onReset}
              variant="outline"
              className="flex-1"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('tryAgain')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
